<script setup>
import { defineProps, ref, computed, defineEmits } from "vue";
import BankPaymentFileView from "./BankPaymentFileView.vue";
import FileUpload from "primevue/fileupload";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Column from "primevue/column";
import Button from "primevue/button";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { dashboardAPI } from "../service/api.js";
import { formatCurrency } from '@/util/format'

const confirm = useConfirm();

const toast = useToast();

const bankPaymentFilesMap = ref({});
const expandedRows = ref({});

const emit = defineEmits(["update", "page", "sort", "filter", "clear-filters"]);

const props = defineProps({
  batches: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    default: () => ({ page: 1, pageSize: 50, totalItems: 0, totalPages: 0 }),
  },
  filterModel: {
    type: Object,
    default: () => ({}),
  },
  sortBy: {
    type: String,
    default: 'load_date',
  },
  sortOrder: {
    type: String,
    default: 'desc',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const filterModelKeys = [
  'id', 'customer_code', 'employer_code', 'source_name', 'funding_status', 'file_status',
  'load_date_from', 'load_date_to', 'processing_date_from', 'processing_date_to', 'sum_min', 'sum_max',
];

// PrimeVue DataTable expects sortOrder as number: 1 = asc, -1 = desc
const sortOrderNum = computed(() => (props.sortOrder === 'asc' ? 1 : -1));

// Display filters for the filter row (from parent filterModel); table does not filter client-side (lazy).
const displayFilters = computed(() => ({
  global: { value: null, matchMode: "contains" },
  ...Object.fromEntries(
    filterModelKeys.map((k) => [k, { value: props.filterModel?.[k] ?? null, matchMode: "contains" }])
  ),
}));

const filterableFields = [
  { field: 'id', placeholder: 'Search by Payment File Id' },
  { field: 'customer_code', placeholder: 'Search by Customer' },
  { field: 'employer_code', placeholder: 'Search by Employer' },
  { field: 'source_name', placeholder: 'Search by Source Name' },
  { field: 'load_date_from', placeholder: 'From (MM-DD-YYYY)' },
  { field: 'load_date_to', placeholder: 'To (MM-DD-YYYY)' },
  { field: 'processing_date_from', placeholder: 'From (MM-DD-YYYY)' },
  { field: 'processing_date_to', placeholder: 'To (MM-DD-YYYY)' },
  { field: 'file_status', placeholder: 'File Status' },
  { field: 'funding_status', placeholder: 'Funding Status' },
  { field: 'sum_min', placeholder: 'Min (cents)' },
  { field: 'sum_max', placeholder: 'Max (cents)' },
];

function emitFilter(updates) {
  emit("filter", { ...props.filterModel, ...updates });
}

const fundingStatuses = ref({
  NEW: { value: "New", order: 0 },
  FUNDS_IN_PROGRESS: { value: "Funds in Progress", order: 1 },
  FUNDS_RECEIVED: { value: "Funds Received", order: 2 },
  PROCESSED: { value: "Processed", order: 3 },
});
const deleteBatch = (batchId) => {
  return dashboardAPI
    .deleteBatch(batchId)
    .then(() => {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Batch deleted successfully",
        life: 3000,
      });
      emit("update");
    })
    .catch((error) => {
      console.error("Error deleting batch:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete batch",
        life: 3000,
      });
    });
};

const confirmDelete = (batchId) => {
  confirm.require({
    message: "Are you sure you want to delete the batch?",
    header: "Confirmation",
    icon: "pi pi-exclamation-triangle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
    },
    accept: () => {
      deleteBatch(batchId);
    },
    reject: () => {
      toast.add({
        severity: "error",
        summary: "Canceled",
        detail: "Batch deletion canceled",
        life: 3000,
      });
    },
  });
};

const fundingStatusOptions = computed(() => {
  return Object.entries(fundingStatuses.value)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, value]) => ({ name: value.value, code: key }));
});

const fileStatusOptions = [
  { label: 'Processed', value: 'Processed' },
  { label: 'Unprocessed', value: 'Unprocessed' },
  { label: 'Partial', value: 'Partial' },
  { label: 'Stuck', value: 'Stuck' },
];

const getFundingStatus = (fundingStatus) => {
  const status = fundingStatuses.value[fundingStatus];
  if (!status) {
    console.error("Funding status not found:", fundingStatus);
    return fundingStatuses.value.NEW.value; // Default to 'New' if not found
  }
  return status.value;
};

function getFilterPlaceholder(field) {
  return filterableFields.find((c) => c.field === field)?.placeholder ?? 'Search';
}

const updateValue = async (data, field, value) => {
  const batchId = data.id;
  try {
    await dashboardAPI.updateBatch(batchId, field, value);
  } catch (error) {
    const detail = error.response?.data || "Failed to update batch";
    toast.add({
      severity: "error",
      summary: "Error",
      detail,
      life: 5000,
    });
    console.error("Error updating batch:", error);
    return;
  }
  const batch = props.batches.find((batch) => batch.id === batchId);
  if (!batch) {
    console.error("Batch not found:", batchId);
    return;
  }
  batch[field] = value;
};

const fetchBankPaymentFiles = async (batchId) => {
  try {
    const response = await dashboardAPI.getBankPaymentFilesByBatchId(batchId);
    return response.data || []; // Adjust based on the API response structure
  } catch (error) {
    console.error("Error fetching bank payment files:", error);
  }
};

const onExpand = async (event) => {
  const batchId = event.data.id;

  const bankPaymentFiles = await fetchBankPaymentFiles(batchId);
  if (bankPaymentFiles) {
    bankPaymentFilesMap.value[batchId] = bankPaymentFiles;
  }
};

const getBankPaymentFiles = (batchId) => {
  return bankPaymentFilesMap.value[batchId] || [];
};

const onPage = (event) => {
  emit("page", event);
};

const onSort = (event) => {
  emit("sort", event);
};

const handleReconciliationFileUpload = async (reconType, event) => {
  try {
    await dashboardAPI.uploadReconciliationFile(reconType, event.files[0]);
    emit("update");
  } catch (error) {
    console.error("Error uploading reconciliation file:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to upload reconciliation file",
      life: 3000,
    });
  }
};
</script>
<template>
  <div>
    <ConfirmDialog />
    <DataTable
      size="large"
      :filters="displayFilters"
      filterDisplay="row"
      :expandedRows="expandedRows"
      :value="batches"
      data-key="id"
      lazy
      paginator
      :first="(pagination.page - 1) * pagination.pageSize"
      :rows="pagination.pageSize"
      :totalRecords="pagination.totalItems"
      :sortField="sortBy"
      :sortOrder="sortOrderNum"
      :loading="loading"
      @page="onPage"
      @sort="onSort"
      @rowExpand="onExpand"
      editMode="cell"
    >
      <template #header>
        <div class="header">
          <Button
            label="Clear filters"
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            @click="emit('clear-filters')"
          />
          <FileUpload
            mode="basic"
            name="reconciliationFile"
            @select="(event) => handleReconciliationFileUpload('A', event)"
            customUpload
            :auto="true"
            chooseLabel="Upload ACH Reconciliation File"
          />
          <FileUpload
            mode="basic"
            name="reconciliationFile"
            @select="(event) => handleReconciliationFileUpload('C', event)"
            customUpload
            :auto="true"
            chooseLabel="Upload CC Reconciliation File"
          />
        </div>
      </template>
      <template #empty>No batches found.</template>
      <Column expander style="width: 5rem" />
      <Column field="id" header="Payment File Id" filterField="id">
        <template #body="slotProps">
          <span :id="`${slotProps.data.id}`">{{ slotProps.data.id }}</span>
        </template>
        <template #filter>
          <InputText
            :modelValue="filterModel?.id ?? ''"
            type="text"
            :placeholder="getFilterPlaceholder('id')"
            @update:modelValue="(v) => emitFilter({ id: v })"
          />
        </template>
      </Column>
      <Column sortable field="customer_code" header="Customer" filterField="customer_code">
        <template #filter>
          <InputText
            :modelValue="filterModel?.customer_code ?? ''"
            type="text"
            :placeholder="getFilterPlaceholder('customer_code')"
            @update:modelValue="(v) => emitFilter({ customer_code: v })"
          />
        </template>
      </Column>
      <Column sortable field="employer_code" header="Employer" filterField="employer_code">
        <template #filter>
          <InputText
            :modelValue="filterModel?.employer_code ?? ''"
            type="text"
            :placeholder="getFilterPlaceholder('employer_code')"
            @update:modelValue="(v) => emitFilter({ employer_code: v })"
          />
        </template>
      </Column>
      <Column sortable field="source_name" header="Source Name" filterField="source_name">
        <template #filter>
          <InputText
            :modelValue="filterModel?.source_name ?? ''"
            type="text"
            :placeholder="getFilterPlaceholder('source_name')"
            @update:modelValue="(v) => emitFilter({ source_name: v })"
          />
        </template>
      </Column>
      <Column sortable field="load_date" header="Upload Date" filterField="load_date_from">
        <template #filter>
          <div class="filter-date-range">
            <InputText
              :modelValue="filterModel?.load_date_from ?? ''"
              type="text"
              :placeholder="getFilterPlaceholder('load_date_from')"
              @update:modelValue="(v) => emitFilter({ load_date_from: v })"
            />
            <InputText
              :modelValue="filterModel?.load_date_to ?? ''"
              type="text"
              :placeholder="getFilterPlaceholder('load_date_to')"
              @update:modelValue="(v) => emitFilter({ load_date_to: v })"
            />
          </div>
        </template>
      </Column>
      <Column sortable field="processing_date" header="Register Date" filterField="processing_date_from">
        <template #filter>
          <div class="filter-date-range">
            <InputText
              :modelValue="filterModel?.processing_date_from ?? ''"
              type="text"
              :placeholder="getFilterPlaceholder('processing_date_from')"
              @update:modelValue="(v) => emitFilter({ processing_date_from: v })"
            />
            <InputText
              :modelValue="filterModel?.processing_date_to ?? ''"
              type="text"
              :placeholder="getFilterPlaceholder('processing_date_to')"
              @update:modelValue="(v) => emitFilter({ processing_date_to: v })"
            />
          </div>
        </template>
      </Column>
      <Column sortable field="file_status" header="File Status" filterField="file_status">
        <template #filter>
          <Select
            :modelValue="filterModel?.file_status ?? null"
            :options="fileStatusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All"
            showClear
            @update:modelValue="(v) => emitFilter({ file_status: v ?? '' })"
          />
        </template>
      </Column>
      <Column
        sortable
        field="funding_status"
        header="Funding Status"
        filterField="funding_status"
      >
        <template #body="slotProps">{{
          getFundingStatus(slotProps.data.funding_status)
        }}</template>
        <template #filter>
          <Select
            :modelValue="filterModel?.funding_status ?? null"
            :options="fundingStatusOptions"
            optionLabel="name"
            optionValue="code"
            placeholder="All"
            showClear
            @update:modelValue="(v) => emitFilter({ funding_status: v ?? '' })"
          />
        </template>
        <template #editor="{ data, field }">
          <Select
            v-model="data[field]"
            :options="fundingStatusOptions"
            optionLabel="name"
            optionValue="code"
            placeholder="Select Status"
            @update:modelValue="(value) => updateValue(data, field, value)"
          />
        </template>
      </Column>
      <Column field="sum" header="Sum" filterField="sum_min">
        <template #filter>
          <div class="filter-date-range">
            <InputText
              :modelValue="filterModel?.sum_min ?? ''"
              type="text"
              inputmode="numeric"
              :placeholder="getFilterPlaceholder('sum_min')"
              @update:modelValue="(v) => emitFilter({ sum_min: (v || '').replace(/[^0-9]/g, '') })"
            />
            <InputText
              :modelValue="filterModel?.sum_max ?? ''"
              type="text"
              inputmode="numeric"
              :placeholder="getFilterPlaceholder('sum_max')"
              @update:modelValue="(v) => emitFilter({ sum_max: (v || '').replace(/[^0-9]/g, '') })"
            />
          </div>
        </template>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.sum) }}
        </template>
      </Column>
      <Column header="Delete Batch">
        <template #body="slotProps">
          <Button
            @click="confirmDelete(slotProps.data.id)"
            icon="pi pi-trash"
            severity="danger"
          />
        </template>
      </Column>
      <template #expansion="slotProps">
        <div>
          <h5>
            Bank Payment Files Count
            {{
              getBankPaymentFiles(slotProps.data.id)
                ? getBankPaymentFiles(slotProps.data.id).length
                : 0
            }}
          </h5>
          <BankPaymentFileView
            size="small"
            :bankPaymentFiles="getBankPaymentFiles(slotProps.data.id)"
            class="level-2-table"
          />
        </div>
      </template>
    </DataTable>
  </div>
</template>
<style scoped>
.header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.filter-date-range {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
/* Base styling for all tables */
:deep(.p-datatable) {
  margin-bottom: 1rem;
}

/* Highlight target batch when navigated via anchor */
span:target {
  background-color: var(--p-highlight-background);
  padding: 0.25rem 0.5rem;
  border-radius: var(--p-border-radius);
  animation: highlight-fade 2s ease-in-out;
}

@keyframes highlight-fade {
  0% {
    background-color: var(--p-primary-200);
  }
  100% {
    background-color: var(--p-highlight-background);
  }
}
</style>
